import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/shared/state/store';
import { UWService } from '@/shared/api/services/uwService';
import { OnboardingService } from '@/shared/api/services/onboardingService';
import SimpleLoadingScreen from '@/components/SimpleLoadingScreen';

// Safety-net guard for the generic IE Hub onboarding routes (/new-onboarding, /onboarding).
// UW customers must never see the generic "How did you hear about IE Hub?" flow —
// they have their own /uw/* journey. RootRoute handles this on the /?scode= entry
// path, but other post-auth redirects (GuestOnly, AcceptTerms, authScreen, etc.)
// fall back to /new-onboarding without a UW check. This guard is the last line of
// defense: if the visitor has a UW application (detected via customerId or email),
// they are redirected to their UW journey screen.

type CheckState = 'checking' | 'allow' | 'redirect';

type UwAppLike = {
  applicationId?: string;
  status?: string;
};

function uwRedirectForStatus(applicationId: string, status: unknown): string {
  const s = typeof status === 'string' ? status.toUpperCase() : '';
  if (s === 'SUBMITTED' || s === 'UNDER_REVIEW') return `/uw/submitted/${applicationId}`;
  if (s === 'APPROVED' || s === 'DECLINED' || s === 'REFERRED') return `/uw/decision/${applicationId}`;
  if (s === 'AFFORDABILITY_DECLINED') return `/uw/affordability-decline/${applicationId}`;
  if (s === 'EXPIRED') return `/uw/expired/${applicationId}`;
  // ACTIVE / IN_PROGRESS / anything else → send to offer screen (start of UW flow)
  return `/uw/offer/${applicationId}`;
}

const BlockUwFromGenericOnboarding: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<CheckState>('checking');
  const [target, setTarget] = useState<string>('/');

  useEffect(() => {
    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        let customerId: string | number | null = null;
        let isUwFlag = false;
        let customerEmail: string | null =
          (user as any)?.emailAddress || (user as any)?.userName || null;

        // Read cached customerData first
        try {
          const raw = typeof window !== 'undefined' ? localStorage.getItem('customerData') : null;
          if (raw) {
            const data = JSON.parse(raw) as Record<string, unknown> | null;
            if (data && (data as any).isUwCustomer === true) isUwFlag = true;
            const maybeId =
              (data as any)?.customerId ?? (data as any)?._id ?? (data as any)?.id ?? null;
            if (typeof maybeId === 'string' || typeof maybeId === 'number') customerId = maybeId;
            if (!customerEmail && typeof (data as any)?.email === 'string') {
              customerEmail = (data as any).email;
            }
          }
        } catch {
          // ignore parse errors
        }

        // If we don't have a customerId yet, fetch it
        if (!customerId && user?.userId) {
          try {
            const resp = await OnboardingService.getCustomerByUserId(user.userId, {
              skipErrorCapture: true,
            });
            const data = resp?.data as Record<string, unknown> | null | undefined;
            if (data) {
              if ((data as any).isUwCustomer === true) isUwFlag = true;
              const maybeId =
                (data as any).customerId ?? (data as any)._id ?? (data as any).id ?? null;
              if (typeof maybeId === 'string' || typeof maybeId === 'number') customerId = maybeId;
              if (!customerEmail && typeof (data as any).email === 'string') {
                customerEmail = (data as any).email;
              }
              try {
                localStorage.setItem('customerData', JSON.stringify(data));
              } catch {
                // ignore storage errors
              }
            }
          } catch {
            // ignore — fall through to email lookup
          }
        }

        let uwApp: UwAppLike | null = null;

        if (isUwFlag && customerId) {
          uwApp = await UWService.getApplicationByCustomerId(customerId);
        }

        if (!uwApp && customerEmail) {
          uwApp = await UWService.getApplicationByEmail(
            customerEmail,
            customerId ?? undefined,
          );
        }

        if (cancelled) return;

        if (uwApp && uwApp.applicationId) {
          setTarget(uwRedirectForStatus(uwApp.applicationId, uwApp.status));
          setState('redirect');
          return;
        }

        setState('allow');
      } catch {
        // Fail-open to avoid locking non-UW users out of onboarding
        if (!cancelled) setState('allow');
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [user?.userId]);

  if (state === 'checking') return <SimpleLoadingScreen title="Loading..." />;
  if (state === 'redirect') return <Navigate to={target} replace />;
  return <Outlet />;
};

export default BlockUwFromGenericOnboarding;
